import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TGetListIntegrationGoogleReviewResponse } from './get-list-integration-google-review.api';
import { GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY } from './get-list-integration-google-review.api';
import { TChannel } from '../../_shared/types/channel.type';

/**
 * Delete Integration Google Review Input Schema
 */
export const deleteGoogleReviewIntegrationValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteGoogleReviewIntegrationValidation = z.infer<typeof deleteGoogleReviewIntegrationValidationSchema>;
export type TDeleteGoogleReviewIntegrationResponse = ApiResponse<TChannel>;

export const deleteGoogleReviewIntegration = async (
  payload: TDeleteGoogleReviewIntegrationValidation
): Promise<TDeleteGoogleReviewIntegrationResponse> => {
  const url = `/integration/google-review/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Google Review Integration React-Query Hook
 */
type UseDeleteGoogleReviewIntegrationOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteGoogleReviewIntegration, AxiosErrorCustom>;
};

export const useDeleteGoogleReviewIntegration = ({ mutationConfig }: UseDeleteGoogleReviewIntegrationOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteGoogleReviewIntegration,
    ...restConfig,
  });
};

/**
 * Use Delete Google Review Integration Optimistically React-Query Hook
 */
type UseDeleteGoogleReviewIntegrationOptimisticallyOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteGoogleReviewIntegration, AxiosErrorCustom>;
};

type TDeleteGoogleReviewIntegrationOptimisticallyContext = {
  previousData: TGetListIntegrationGoogleReviewResponse | undefined;
};

export const useDeleteGoogleReviewIntegrationOptimistically = ({
  mutationConfig,
}: UseDeleteGoogleReviewIntegrationOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteGoogleReviewIntegration,
    onMutate: async (data: TDeleteGoogleReviewIntegrationValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetListIntegrationGoogleReviewResponse>([
        GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY,
      ]);

      queryClient.setQueriesData<TGetListIntegrationGoogleReviewResponse>(
        { queryKey: [GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (integration) => integration.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteGoogleReviewIntegrationOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteGoogleReviewIntegrationOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY],
          (context as TDeleteGoogleReviewIntegrationOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
